/**
 * Pure utility functions for managing agent enabledActions.
 *
 * All functions are side-effect-free: they accept plain arrays/objects
 * and return new arrays without mutating inputs or making API calls.
 */

/**
 * Scheduling tools that are always auto-added when a cron connector is installed.
 */
const CRON_SCHEDULING_TOOLS = ['schedule_task', 'cancel_task', 'list_tasks'];

/**
 * Merges new tool names into an existing enabledActions array.
 *
 * - If the connector has `fromTemplate: true` AND a non-empty `enabledTools`,
 *   only those tools are added.
 * - Otherwise, ALL tool names from `connectorType.capabilities.tools` are added.
 * - When the connectorType is `cron`, the scheduling tools (schedule_task,
 *   cancel_task, list_tasks) are always included regardless of template settings.
 * - The result is the set union (deduplicated) of current + new tools.
 *
 * @param {string[]} currentActions - current enabledActions on the agent
 * @param {object}   connectorType  - ConnectorType record with capabilities.tools[]
 * @param {object}   [connector]    - Connector record (may have fromTemplate, enabledTools)
 * @returns {string[]} merged enabledActions (new array)
 */
export function mergeEnabledActions(currentActions, connectorType, connector) {
  const current = Array.isArray(currentActions) ? currentActions : [];

  let newTools;
  if (connector?.fromTemplate && Array.isArray(connector.enabledTools) && connector.enabledTools.length > 0) {
    newTools = connector.enabledTools;
  } else {
    newTools = (connectorType?.capabilities?.tools || []).map(t => t.name);
  }

  // When a cron connector is installed, always include scheduling tools
  if (connectorType?.type === 'cron') {
    newTools = [...new Set([...newTools, ...CRON_SCHEDULING_TOOLS])];
  }

  return [...new Set([...current, ...newTools])];
}

/**
 * Adds or removes a single tool name from enabledActions.
 *
 * @param {string[]} currentActions - current enabledActions
 * @param {string}   toolName       - tool to toggle
 * @param {boolean}  enabled        - true → add, false → remove
 * @returns {string[]} updated enabledActions (new array)
 */
export function toggleTool(currentActions, toolName, enabled) {
  const current = Array.isArray(currentActions) ? currentActions : [];

  if (enabled) {
    if (current.includes(toolName)) return [...current];
    return [...current, toolName];
  }
  return current.filter(name => name !== toolName);
}

/**
 * Removes tools belonging to a deleted connector from enabledActions,
 * unless another installed connector also provides them.
 *
 * @param {string[]} currentActions        - current enabledActions
 * @param {object}   removedConnectorType  - ConnectorType of the removed connector
 * @param {object[]} remainingConnectors   - connectors still installed (each has `type`)
 * @param {object[]} connectorTypes        - all ConnectorType records
 * @returns {string[]} cleaned enabledActions (new array)
 */
export function cleanupAfterRemoval(currentActions, removedConnectorType, remainingConnectors, connectorTypes) {
  const current = Array.isArray(currentActions) ? currentActions : [];

  // Tools that belonged to the removed connector
  const removedTools = (removedConnectorType?.capabilities?.tools || []).map(t => t.name);
  if (removedTools.length === 0) return [...current];

  // Tools still provided by remaining connectors
  const stillProvided = new Set();
  for (const rc of (remainingConnectors || [])) {
    const ct = (connectorTypes || []).find(t => t.type === rc.type);
    if (ct?.capabilities?.tools) {
      ct.capabilities.tools.forEach(t => stillProvided.add(t.name));
    }
  }

  // Keep a tool if it was NOT from the removed connector, or if it's still provided
  return current.filter(name => !removedTools.includes(name) || stillProvided.has(name));
}

/**
 * Returns tools grouped by connector for display, excluding orphaned tools
 * (tools in enabledActions that are not backed by any installed connector).
 *
 * @param {string[]} enabledActions       - current enabledActions on the agent
 * @param {object[]} installedConnectors  - installed connectors (each has `type`, `connectorId`)
 * @param {object[]} connectorTypes       - all ConnectorType records
 * @returns {Array<{connector: object, connectorType: object, tools: Array<{name: string, description: string, enabled: boolean}>}>}
 */
export function getDisplayableTools(enabledActions, installedConnectors, connectorTypes) {
  const actions = Array.isArray(enabledActions) ? enabledActions : [];
  const connectors = Array.isArray(installedConnectors) ? installedConnectors : [];
  const ctypes = Array.isArray(connectorTypes) ? connectorTypes : [];

  const groups = [];

  for (const conn of connectors) {
    const ct = ctypes.find(t => t.type === conn.type);
    if (!ct?.capabilities?.tools || ct.capabilities.tools.length === 0) continue;

    const tools = ct.capabilities.tools.map(tool => ({
      name: tool.name,
      description: tool.description || '',
      enabled: actions.includes(tool.name),
      output_fields: tool.output_fields || null,
    }));

    groups.push({
      connector: conn,
      connectorType: ct,
      tools,
    });
  }

  return groups;
}
